import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MessageCircle, MapPin, Calendar, Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const faqs = [
  {
    question: "How long before I see results?",
    answer:
      "Most clients see initial improvements within 30-60 days, with significant results by month 3. We provide regular reporting so you can track progress from day one.",
  },
  {
    question: "What's the minimum engagement?",
    answer:
      "We work on 3-month minimum engagements to ensure we have enough time to implement, optimize, and demonstrate measurable results.",
  },
  {
    question: "Do you work with startups?",
    answer:
      "Yes! We specialize in B2B companies including SaaS startups from Seed to Series B. Our minimum retainer is $2,000/month.",
  },
  {
    question: "Can you work with our existing tools?",
    answer:
      "Absolutely. We integrate with most marketing and sales tools including HubSpot, Salesforce, Google Ads, Meta, LinkedIn, and more.",
  },
];

const contactFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name must be less than 50 characters"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name must be less than 50 characters"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email must be less than 255 characters"),
  company: z.string().trim().min(1, "Company is required").max(100, "Company must be less than 100 characters"),
  budget: z.string().optional(),
  serviceInterest: z.string().optional(),
  message: z.string().trim().max(2000, "Message must be less than 2000 characters").optional(),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const initialFormData: ContactFormData = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  budget: "",
  serviceInterest: "",
  message: "",
};

export default function Contact() {
  const { toast } = useToast();
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitToHubSpot = async (data: ContactFormData) => {
    const portalId = "48130722";
    const formGuid = "b7f57fa9-dbf9-46dc-ac9c-1b60304fe7d1";

    const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formGuid}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fields: [
          { name: "firstname", value: data.firstName },
          { name: "lastname", value: data.lastName },
          { name: "email", value: data.email },
          { name: "company", value: data.company },
          { name: "monthly_budget", value: data.budget || "" },
          { name: "services_interest", value: data.serviceInterest || "" },
          { name: "tell_us_about_your_goals", value: data.message || "" },
        ],
        context: {
          pageUri: window.location.href,
          pageName: "Contact Page - Book Strategy Call",
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Submission failed");
    }
    return response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = contactFormSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitToHubSpot(result.data);
      toast({
        title: "Message sent!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData(initialFormData);
    } catch (error) {
      toast({
        title: "Submission failed",
        description: "Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 to-background pointer-events-none" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="heading-hero mb-6">Let's Talk Growth</h1>
            <p className="text-xl text-muted-foreground">
              Book a free strategy call to discover how AI-powered marketing can transform your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <AnimatedSection>
              <div className="bg-card rounded-3xl border border-border p-8 md:p-10">
                <h2 className="text-2xl font-semibold mb-8">Book a Strategy Call</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => updateField("firstName", e.target.value)}
                        className={cn("mt-2", errors.firstName && "border-destructive")}
                        required
                      />
                      {errors.firstName && <p className="text-sm text-destructive mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => updateField("lastName", e.target.value)}
                        className={cn("mt-2", errors.lastName && "border-destructive")}
                        required
                      />
                      {errors.lastName && <p className="text-sm text-destructive mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Work Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={cn("mt-2", errors.email && "border-destructive")}
                      required
                    />
                    {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => updateField("company", e.target.value)}
                      className={cn("mt-2", errors.company && "border-destructive")}
                      required
                    />
                    {errors.company && <p className="text-sm text-destructive mt-1">{errors.company}</p>}
                  </div>

                  <div>
                    <Label htmlFor="budget">Monthly Marketing Budget</Label>
                    <Select value={formData.budget} onValueChange={(value) => updateField("budget", value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2k-5k">$2,000 - $5,000</SelectItem>
                        <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                        <SelectItem value="10k-25k">$10,000 - $25,000</SelectItem>
                        <SelectItem value="25k+">$25,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="interest">What are you interested in?</Label>
                    <Select
                      value={formData.serviceInterest}
                      onValueChange={(value) => updateField("serviceInterest", value)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="geo">GEO (Generative Engine Optimization)</SelectItem>
                        <SelectItem value="ai-ads">AI-Powered Paid Ads</SelectItem>
                        <SelectItem value="ai-content">AI Content & SEO</SelectItem>
                        <SelectItem value="ai-agents">AI Agents & Automation</SelectItem>
                        <SelectItem value="full-service">Full-Service Package</SelectItem>
                        <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Tell us about your goals</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => updateField("message", e.target.value)}
                      className={cn("mt-2 min-h-[120px]", errors.message && "border-destructive")}
                      placeholder="What challenges are you facing? What results are you hoping to achieve?"
                    />
                    {errors.message && <p className="text-sm text-destructive mt-1">{errors.message}</p>}
                  </div>

                  <Button type="submit" size="lg" className="w-full rounded-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Book Strategy Call
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-muted-foreground text-center">Free 30-minute call. No obligation.</p>
                </form>
              </div>
            </AnimatedSection>

            {/* Info */}
            <div className="space-y-12">
              {/* Calendar Embed Placeholder */}
              <AnimatedSection delay={0.1}>
                <div className="bg-secondary/30 rounded-3xl p-8 md:p-10 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">Prefer to Book Directly?</h3>
                  <p className="text-muted-foreground mb-6">Choose a time that works for you on our calendar.</p>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    data-cal-link="thecloudseo/30-min-meeting"
                    data-cal-namespace="30-min-meeting"
                    data-cal-config='{"layout":"month_view"}'
                  >
                    Open Calendar
                  </Button>
                </div>
              </AnimatedSection>

              {/* Contact Info */}
              <AnimatedSection delay={0.2}>
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold">Get in Touch</h3>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a href="mailto:hello@thecloudseo.com" className="font-medium hover:underline">
                        hello@thecloudseo.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center">
                      <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">WhatsApp</p>
                      <a
                        href="https://api.whatsapp.com/send/?phone=916377319652&text=+Interested%2C+tell+me+more+about+your+services%2C+specifically+about&type=phone_number&app_absent=0"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline"
                      >
                        Chat with us
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Global Presence</p>
                      <p className="font-medium">United States • India • Australia • UAE</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* FAQ */}
              <AnimatedSection delay={0.3}>
                <h3 className="text-xl font-semibold mb-6">Common Questions</h3>
                <Accordion type="single" collapsible className="space-y-3">
                  {faqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`faq-${index}`}
                      className="border border-border rounded-2xl px-6 data-[state=open]:bg-secondary/30"
                    >
                      <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-4">{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
